/**
 * Super-admin pump analytics — Data Analytics and Owner Dashboard (embedded owner views).
 */
import OwnerAnalyticsReport from '../Owner/OwnerAnalyticsReport.component';
import OwnerDashboard from '../Owner/OwnerDashboard.component';
import { downloadPumpAnalyticsPdf } from './adminApi';
import AdminPumpReportExportButton from './AdminPumpReportExportButton';
import { ADMIN_PUMP_EXPORT_IDS } from './adminPumpReportSections';
import swal from 'sweetalert';

export function AdminPumpDataAnalyticsPanel({
  pumpId,
  pumpName = '',
  syncDateRange = null,
  dateRangeKey = 0,
  hideChildDateFilter = true,
  hideHeader = false,
  onReportLoaded = null,
}) {
  const fromDate = syncDateRange?.from_date || '';
  const toDate = syncDateRange?.to_date || '';

  function exportServerPdf() {
    downloadPumpAnalyticsPdf(pumpId, fromDate || undefined, toDate || undefined)
      .catch((err) => swal('Error', err.message, 'error'));
  }

  const exportActions = (
    <div className="d-flex flex-wrap gap-2">
      <AdminPumpReportExportButton
        targetId={ADMIN_PUMP_EXPORT_IDS.dataAnalytics}
        filename={`pump-${pumpId}-data-analytics.pdf`}
        label="Export screen PDF"
        disabled={!fromDate || !toDate}
        metadata={{
          title: `${pumpName || 'Pump'} — Data Analytics`,
          subtitle: fromDate && toDate ? `${fromDate} to ${toDate}` : '',
        }}
      />
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={exportServerPdf} disabled={!fromDate || !toDate}>
        Summary PDF
      </button>
    </div>
  );

  return (
    <div className="admin-pump-analytics-panel" id={ADMIN_PUMP_EXPORT_IDS.dataAnalytics}>
      {hideHeader ? (
        <div className="d-flex flex-wrap justify-content-end gap-2 mb-3">{exportActions}</div>
      ) : (
        <div className="admin-panel mb-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Data Analytics</h5>
              <p className="small text-muted mb-0">
                Executive health report, deep analysis, cash &amp; stock intelligence, and profit playbook.
                {fromDate && toDate && (
                  <>
                    {' '}
                    <span className="text-body-secondary">
                      Period: <strong>{fromDate}</strong> → <strong>{toDate}</strong>
                    </span>
                  </>
                )}
              </p>
            </div>
            {exportActions}
          </div>
        </div>
      )}

      <OwnerAnalyticsReport
        key={`analytics-${dateRangeKey}-${fromDate}-${toDate}`}
        embedded
        pumpId={Number(pumpId)}
        defaultFromDate={fromDate}
        defaultToDate={toDate}
        syncDateRange={syncDateRange}
        dateRangeKey={dateRangeKey}
        hideDateFilter={hideChildDateFilter}
        onReportLoaded={onReportLoaded}
      />
    </div>
  );
}

export function AdminPumpOwnerDashboardPanel({
  pumpId,
  pumpName = '',
  syncDateRange = null,
  dateRangeKey = 0,
  hideChildDateFilter = true,
  hideHeader = false,
}) {
  const fromDate = syncDateRange?.from_date || '';
  const toDate = syncDateRange?.to_date || '';

  const exportBtn = (
    <AdminPumpReportExportButton
      targetId={ADMIN_PUMP_EXPORT_IDS.ownerDashboard}
      filename={`pump-${pumpId}-owner-dashboard.pdf`}
      label="Export screen PDF"
      disabled={!fromDate || !toDate}
      metadata={{
        title: `${pumpName || 'Pump'} — Owner Dashboard`,
        subtitle: fromDate && toDate ? `${fromDate} to ${toDate}` : '',
      }}
    />
  );

  return (
    <div className="admin-pump-analytics-panel" id={ADMIN_PUMP_EXPORT_IDS.ownerDashboard}>
      {hideHeader ? (
        <div className="d-flex flex-wrap justify-content-end gap-2 mb-3">{exportBtn}</div>
      ) : (
        <div className="admin-panel mb-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Owner Dashboard</h5>
              <p className="small text-muted mb-0">
                Sales, profit, fuel mix, stock, expenses, employees, nozzles, purchases, and customer charts.
              </p>
            </div>
            {exportBtn}
          </div>
        </div>
      )}
      <OwnerDashboard
        key={`dashboard-${dateRangeKey}-${fromDate}-${toDate}`}
        embedded
        pumpId={Number(pumpId)}
        defaultFromDate={fromDate}
        defaultToDate={toDate}
        syncDateRange={syncDateRange}
        dateRangeKey={dateRangeKey}
        hideDateFilter={hideChildDateFilter}
      />
    </div>
  );
}
